import { useState, useMemo, useEffect } from 'react'
import {
  FilterState,
  LeadRecord,
  OpportunityRecord,
  LoanOfficer,
} from '../types/throughput'
import { generateMockData } from '../utils/mockThroughputData'
import {
  filterData,
  calculateFunnelMetrics,
  calculateOfficerMetrics,
} from '../utils/throughputCalculations'
import { subDays, format } from 'date-fns'

export function useThroughputData() {
  const [data, setData] = useState<{
    leads: LeadRecord[]
    opportunities: OpportunityRecord[]
    officers: LoanOfficer[]
  } | null>(null)

  const [filters, setFilters] = useState<FilterState>({
    dateRange: {
      start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
      end: format(new Date(), 'yyyy-MM-dd'),
    },
    scopeType: 'All',
    includeInactive: false,
    leadSources: [],
    groupBySource: false,
  })

  useEffect(() => {
    // Simulate API load
    const mock = generateMockData()
    setData(mock)
  }, [])

  const filteredData = useMemo(() => {
    if (!data) return null
    return filterData(data.leads, data.opportunities, data.officers, filters)
  }, [data, filters])

  const metrics = useMemo(() => {
    if (!filteredData || !data) return null

    const funnelMetrics = calculateFunnelMetrics(
      filteredData.filteredLeads,
      filteredData.filteredOpportunities,
    )

    // Filter officers to only those in scope for the table
    const scopeOfficers = data.officers.filter((o) =>
      filteredData.scopedOfficerIds.includes(o.id),
    )
    const officerMetrics = calculateOfficerMetrics(
      scopeOfficers,
      filteredData.filteredLeads,
      filteredData.filteredOpportunities,
    )

    return {
      funnelMetrics,
      officerMetrics,
      totalLeads: filteredData.filteredLeads.length,
      totalConverted: filteredData.filteredOpportunities.length,
      totalFunded: filteredData.filteredOpportunities.filter(
        (o) => o.fundedDate,
      ).length,
    }
  }, [filteredData, data])

  return {
    isLoading: !data,
    filters,
    setFilters,
    metrics,
    officers: data?.officers || [],
    filteredLeads: filteredData?.filteredLeads || [],
    filteredOpportunities: filteredData?.filteredOpportunities || [],
  }
}
